import { addEventProcessor } from '@sentry/browser';

/**
 * The session facts a hydration mismatch is worth nothing without.
 *
 * Assisted service lives in localStorage, so a server render cannot see one: getAssistedService
 * returns null under IS_SSR, and the server learns of a session only through the forwarded
 * `as-shop-type` cookie. shopType therefore has two different sources on the two sides, and it
 * reaches the product family query's variables through isClosedShop() and the assortment flags -
 * which is how a session ends up deciding whether the client can restore the server's payload at
 * all. A report that says only "children mismatch on div.accordion-customizable-fi__panels" cannot
 * be told apart from one on an anonymous page; these three tags can.
 */

export interface SessionTags {
  /** 'unknown' where it could not be read - never absent, so the Sentry tag is always searchable. */
  shopType: string;
  loggedIn: boolean;
  assistedService: boolean;
}

/** Structural on purpose: the real plugin satisfies it, and a test does not need all of it. */
export interface SessionSource {
  shop?: { shopType?: () => string };
  user?: { authenticated?: () => boolean };
  getAssistedService?: () => unknown;
}

/** Reporting must not be the reason an event goes missing, so every read is guarded. */
const attempt = <T>(read: () => T, fallback: T): T => {
  try {
    return read() ?? fallback;
  } catch {
    return fallback;
  }
};

export const describeSession = (fischer: SessionSource | null | undefined): SessionTags => ({
  shopType: attempt(() => fischer?.shop?.shopType?.(), 'unknown') || 'unknown',
  loggedIn: attempt(() => Boolean(fischer?.user?.authenticated?.()), false),
  assistedService: attempt(() => Boolean(fischer?.getAssistedService?.()), false),
});

/** Sentry's own tag bag, narrowed to what this writes. */
interface TaggedEvent {
  tags?: Record<string, unknown>;
}

export const withSessionTags = <T extends TaggedEvent>(event: T, session: SessionTags): T => ({
  ...event,
  tags: {
    ...event.tags,
    'APP shopType': session.shopType,
    'APP loggedIn': session.loggedIn,
    'APP assistedService': session.assistedService,
  },
});

let installed = false;

/**
 * Puts the session on every event, not only the hydration ones - an error means something different
 * in an assisted service session than on an anonymous page, and that is not visible from a stack.
 *
 * Registered once and read at capture time: `box.plugin` is filled in when the first app installs
 * the plugin, and a login or an assisted service session started mid-page has to be reflected
 * rather than frozen. Idempotent because hydrateAllApps runs again for late components and the
 * forms app is its own entry point.
 */
export const installSessionTagProcessor = (box: { plugin: SessionSource | null }): void => {
  if (installed) {
    return;
  }
  installed = true;
  addEventProcessor((event) => withSessionTags(event, describeSession(box.plugin)));
};
